// Maps a Solomon edge-function failure into a user-facing message.
//
// WHY: the chat/inline UIs both flattened every failure to "Solomon
// is unavailable right now", which made a missing ANTHROPIC_API_KEY
// (a self-service admin fix) indistinguishable from a transient
// outage. The edge functions now return a stable `code`
// (ai_not_configured | ai_auth | ai_model | ai_rate_limited |
// solomon_error). This translates that code — wherever it surfaces
// in the thrown error or response body — into actionable copy, while
// still degrading to a generic message for unknown failures.
//
// Pure, dependency-free, unit-tested. The error shape from the edge
// wrapper is not strongly typed, so detection is intentionally
// defensive (string scan over the serialized error + message).

const MESSAGE_BY_CODE: Record<string, string> = {
  ai_not_configured:
    "Solomon isn't set up yet — an administrator needs to add the AI API key (ANTHROPIC_API_KEY).",
  ai_auth:
    "Solomon's AI key was rejected. An administrator needs to update ANTHROPIC_API_KEY.",
  ai_model:
    "Solomon's AI model is misconfigured. An administrator needs to update the SOLOMON_MODEL setting.",
  ai_rate_limited:
    "Solomon is busy right now (rate limited). Please try again in a moment.",
  feature_not_available:
    "Solomon AI is available on the Flourish plan. Upgrade in Settings → Subscription to use it.",
  entitlement_check_failed:
    "Solomon couldn't verify your plan right now. Please try again in a moment.",
  organization_required:
    "Select an organization before asking Solomon.",
};

const GENERIC = "Solomon is unavailable right now. Try again later.";

/**
 * @param err   the thrown error from invokeEdgeFunction (unknown shape)
 * @param data  optional response body, if the caller has it
 */
export function solomonErrorMessage(err: unknown, data?: unknown): string {
  const haystack = [
    safeStringify(err),
    typeof (err as { message?: unknown })?.message === "string"
      ? (err as { message: string }).message
      : "",
    safeStringify(data),
  ]
    .join(" ")
    .toLowerCase();

  for (const code of Object.keys(MESSAGE_BY_CODE)) {
    if (haystack.includes(code)) return MESSAGE_BY_CODE[code];
  }
  return GENERIC;
}

function safeStringify(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
